import type { ITextContent } from '../../../Scripts/search';

interface IFilterOption {
  id: number;
  name: string;
  count: number;
  parentid: number;
}

export interface ISearchFilter {
  id: number;
  heading: string;
  filteroptions: IFilterOption[];
}

export interface IRenderFiltersOptions {
  container?: HTMLElement | null;
  preselectedIds?: number[];
  onChange?: (args: {
    filterId: number;
    selectedIds: number[];
    selectedLabels: string[];
    root: HTMLElement;
  }) => void;
}

const renderCleanupByContainer = new WeakMap<HTMLElement, () => void>();

export function renderFilters(filters: ISearchFilter[], translations: ITextContent, opts: IRenderFiltersOptions = {}): void {
  const { onChange, container, preselectedIds } = opts;
  const preSet = new Set(preselectedIds ?? []);
  const filterHeading = document.querySelector<HTMLHeadingElement>(".search__filter-heading");
  const searchFilterSection = container ?? document.querySelector<HTMLElement>(".search__filter-container");
  const globalClearButton = document.querySelector<HTMLElement>("#clearAllFilters");

  const updateGlobalClearVisibility = () => {
    if (searchFilterSection) {
      const anyChecked = searchFilterSection.querySelector<HTMLInputElement>(".filter-dropdown__option-checkbox:checked");

      if (globalClearButton) {
        globalClearButton.hidden = !anyChecked && preSet.size === 0;
      }
    }
  };

  if (!searchFilterSection) {
    return;
  }

  const expandedButton = searchFilterSection.querySelector<HTMLButtonElement>('.filter-dropdown__button[aria-expanded="true"]');
  const expandedDropdown = expandedButton?.closest<HTMLElement>('.filter-dropdown');
  const expandedFilterId = expandedDropdown?.dataset.filterId;
  const activeElement = document.activeElement;
  const shouldRestoreFocus = Boolean(expandedDropdown && activeElement && expandedDropdown.contains(activeElement));
  const focusedOptionId = shouldRestoreFocus && activeElement instanceof HTMLInputElement &&
    activeElement.classList.contains('filter-dropdown__option-checkbox')
    ? activeElement.dataset.optionId
    : undefined;

  renderCleanupByContainer.get(searchFilterSection)?.();
  renderCleanupByContainer.delete(searchFilterSection);

  const cleanupCallbacks: Array<() => void> = [];
  if (globalClearButton) {
    const onGlobalClear = () => {
      const allCheckboxes = searchFilterSection.querySelectorAll<HTMLInputElement>(".filter-dropdown__option-checkbox");
      allCheckboxes.forEach(c => (c.checked = false));
      preSet.clear();

      searchFilterSection.querySelectorAll<HTMLElement>(".filter-dropdown").forEach(dropdown => {
        const buttonText = dropdown.querySelector<HTMLElement>(".filter-dropdown__selected");
        if (buttonText) {
          buttonText.textContent = translations.filterPlaceHolder;
        }

        const clearOption = dropdown.querySelector<HTMLElement>('.filter-clear-button')
          ?.closest<HTMLLIElement>('.filter-dropdown__option');
        if (clearOption) {
          clearOption.hidden = true;
        }
      });

      updateGlobalClearVisibility();
      onChange?.({
        filterId: -1,
        selectedIds: [],
        selectedLabels: [],
        root: searchFilterSection,
      });
    };

    globalClearButton.addEventListener('click', onGlobalClear);
    cleanupCallbacks.push(() => globalClearButton.removeEventListener('click', onGlobalClear));
  }

  renderCleanupByContainer.set(searchFilterSection, () => {
    cleanupCallbacks.forEach(cleanup => cleanup());
  });

  // clear old
  searchFilterSection.replaceChildren();

  const hasFilters = filters && filters.length > 0;

  if (filterHeading) {
    filterHeading.hidden = !hasFilters;
  }

  if (!hasFilters) {
    updateGlobalClearVisibility();
    return;
  }

  const dropdowns: Array<{
    root: HTMLDivElement;
    close: () => void;
  }> = [];
  const dropdownsToRestore: Array<{
    open: () => void;
    focusTarget: HTMLElement | null;
  }> = [];

  filters.forEach((filter) => {
    const filterDropdown = document.createElement('div') as HTMLDivElement;
    filterDropdown.classList.add('filter-dropdown');
    filterDropdown.dataset.filterId = String(filter.id);
    searchFilterSection.appendChild(filterDropdown);

    const filterLabel = document.createElement('span') as HTMLSpanElement;
    filterLabel.classList.add('filter-dropdown__label');
    filterLabel.textContent = filter.heading;
    filterDropdown.appendChild(filterLabel);

    const filterButton = document.createElement('button') as HTMLButtonElement;
    filterButton.classList.add('button', 'button--secondary', 'filter-dropdown__button');
    filterButton.type = 'button';
    filterButton.ariaExpanded = 'false';
    filterDropdown.appendChild(filterButton);

    const buttonText = document.createElement('span') as HTMLSpanElement;
    buttonText.classList.add('filter-dropdown__selected');
    filterButton.appendChild(buttonText);

    const buttonIcon = document.createElement('span') as HTMLSpanElement;
    buttonIcon.classList.add('filter-dropdown__icon', 'icon', 'icon--chevron-down');
    filterButton.appendChild(buttonIcon);

    const panel = document.createElement('fieldset');
    panel.id = filter.heading;
    panel.classList.add('filter-dropdown__fieldset');
    panel.hidden = true;
    filterDropdown.appendChild(panel);

    const filterOptionsList = document.createElement('ul') as HTMLUListElement;
    filterOptionsList.classList.add('filter-dropdown__option-list');
    panel.appendChild(filterOptionsList);

    // Helper to create a checkbox row
    const createRow = (
      value: string,
      labelText: string,
      checked = true): HTMLInputElement => {
      const li = document.createElement('li');
      li.className = 'filter-dropdown__option';

      const label = document.createElement('label');
      label.className = 'filter-dropdown__option-label';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'filter-dropdown__option-checkbox';
      input.value = value;
      input.checked = checked;

      input.dataset.label = labelText;
      input.dataset.optionId = value;

      const text = document.createElement('span');
      text.className = 'filter-dropdown__option-text';
      text.textContent = labelText;

      label.appendChild(input);
      label.appendChild(text);
      li.appendChild(label);
      filterOptionsList.appendChild(li);
      return input;
    };

    //clearAll at the top
    const clearAll = document.createElement('li') as HTMLLIElement;
    clearAll.className = 'filter-dropdown__option';
    clearAll.hidden = true;
    filterOptionsList.appendChild(clearAll);

    const clearAllButton = document.createElement('button') as HTMLButtonElement;
    clearAllButton.classList.add('filter-clear-button', 'link');
    clearAll.appendChild(clearAllButton);

    const clearAllIcon = document.createElement('span') as HTMLSpanElement;
    clearAllIcon.classList.add('filter-clear-button__icon', 'icon', 'icon--close');
    clearAllButton.appendChild(clearAllIcon);

    const clearAllText = document.createElement('span') as HTMLSpanElement;
    clearAllText.textContent = 'Rensa';
    clearAllButton.appendChild(clearAllText);

    const optionCheckboxes: HTMLInputElement[] = (filter.filteroptions ?? []).map(opt => {
      const isChecked = preSet.has(opt.id);
      // value is the id as string; label is name
      return createRow(String(opt.id), opt.name, isChecked);
    });

    let isInit = true;
    let isSyncing = false;

    const close = () => {
      filterButton.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
    };

    const open = () => {
      filterButton.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
    };

    dropdowns.push({ root: filterDropdown, close });

    const getSelected = (): { selectedIds: number[]; selectedLabels: string[] } => {
      const selected = optionCheckboxes.filter(c => c.checked);
      const selectedIds = selected
        .map(c => Number(c.dataset.optionId))
        .filter((n): n is number => Number.isFinite(n)); // type guard

      const selectedLabels = selected
        .map(c => c.dataset.label)
        .filter((s): s is string => typeof s === 'string' && s.length > 0);

      return { selectedIds, selectedLabels };
    };

    const updateClearAllVisibility = () => {
      const { selectedIds } = getSelected();
      clearAll.hidden = selectedIds.length === 0;   // show only when something is selected
    };

    const updateButtonSummary = () => {
      const { selectedLabels } = getSelected();

      if (selectedLabels.length === 0) {
        buttonText.textContent = translations.filterPlaceHolder;
      } else if (selectedLabels.length <= 2) {
        buttonText.textContent = selectedLabels.join(', ');
      } else {
        buttonText.textContent = `${selectedLabels.length} valda`;
      }

      updateClearAllVisibility();
    };

    const setChildrenFromClearAll = () => {
      optionCheckboxes.forEach(c => {
        c.checked = false;
        preSet.delete(Number(c.dataset.optionId));
      });
    };

    const emitChange = () => {
      if (isInit || isSyncing) return;

      const { selectedIds, selectedLabels } = getSelected();
      onChange?.({
        filterId: filter.id,
        selectedIds,
        selectedLabels,
        root: filterDropdown,
      });
    };

    // Toggle open/close
    filterButton.addEventListener("click", (event) => {
      event.stopPropagation();
      const expanded = filterButton.getAttribute('aria-expanded') === 'true';
      if (expanded) {
        close();
      } else {
        open();
      }
    });

    clearAllButton.addEventListener('click', () => {
      isSyncing = true;
      setChildrenFromClearAll();
      isSyncing = false;
      updateButtonSummary();
      emitChange();
      updateGlobalClearVisibility();
      close();
      filterButton.focus();
    });

    optionCheckboxes.forEach((c) => {
      c.addEventListener('change', () => {
        if (isSyncing) return;
        const optionId = Number(c.dataset.optionId);
        if (c.checked) {
          preSet.add(optionId);
        } else {
          preSet.delete(optionId);
        }
        updateButtonSummary();
        emitChange();
        updateGlobalClearVisibility();
      });
    });

    // Close on Escape when panel is open
    panel.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close();
        filterButton.focus();
      }
    });

    // Initial state
    updateButtonSummary();
    emitChange();
    isInit = false;

    if (expandedFilterId === String(filter.id)) {
      const focusedCheckbox = focusedOptionId
        ? optionCheckboxes.find(checkbox => checkbox.dataset.optionId === focusedOptionId)
        : undefined;

      dropdownsToRestore.push({
        open,
        focusTarget: shouldRestoreFocus ? focusedCheckbox ?? filterButton : null,
      });
    }
  });

  const onDocumentPointerDown = (event: PointerEvent) => {
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    dropdowns.forEach(({ root, close }) => {
      if (!root.contains(target)) {
        close();
      }
    });
  };
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  cleanupCallbacks.push(() => {
    document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  });

  const dropdownToRestore = dropdownsToRestore[0];
  dropdownToRestore?.open();
  dropdownToRestore?.focusTarget?.focus();

  updateGlobalClearVisibility();
}
